import { z } from 'zod';
import {
    cardSchema,
    conversationDetailSchema,
    conversationListSchema,
} from '@ctd/agent-contract';
import type { ConversationDetail, ConversationList } from '@ctd/agent-contract';

const CONVERSATIONS_URL = `${
    import.meta.env.REACT_APP_API_URL || ''
}/api-cms/agent/conversations`;

const UNREADABLE_CARD = 'unreadable card';

/** A 401 from the agent routes: the session is over, not the request. */
export class AgentUnauthorizedError extends Error {}

async function getJson(
    fetchImpl: typeof fetch,
    url: string,
    headers: Record<string, string>
): Promise<unknown> {
    const response = await fetchImpl(url, { method: 'GET', headers });
    if (response.status === 401) {
        throw new AgentUnauthorizedError('Agent conversations: unauthorized');
    }
    if (!response.ok) {
        throw new Error(
            `Agent conversations request failed: ${response.status}`
        );
    }
    return response.json();
}

const storedCallsSchema = z.looseObject({
    turns: z.array(
        z.looseObject({
            answer: z
                .looseObject({
                    calls: z.array(
                        z.looseObject({ card: z.unknown().optional() })
                    ),
                })
                .nullable(),
        })
    ),
});

function failIfUnreadable(call: { card?: unknown }): object {
    if (call.card === undefined || cardSchema.safeParse(call.card).success) {
        return call;
    }
    return {
        ...Object.fromEntries(
            Object.entries(call).filter(([key]) => key !== 'card')
        ),
        error: UNREADABLE_CARD,
    };
}

// A card stored under an older contract fails its own call, not the whole conversation.
function failUnreadableCards(body: unknown): unknown {
    const stored = storedCallsSchema.safeParse(body);
    if (!stored.success) {
        return body;
    }
    return {
        ...stored.data,
        turns: stored.data.turns.map((turn) =>
            turn.answer
                ? {
                      ...turn,
                      answer: {
                          ...turn.answer,
                          calls: turn.answer.calls.map(failIfUnreadable),
                      },
                  }
                : turn
        ),
    };
}

/** One page of the caller's conversations, most recent activity first. */
export async function fetchConversationPage(
    fetchImpl: typeof fetch,
    headers: Record<string, string>,
    page: number
): Promise<ConversationList> {
    return conversationListSchema.parse(
        await getJson(fetchImpl, `${CONVERSATIONS_URL}?page=${page}`, headers)
    );
}

/** One conversation with its last stored turns. */
export async function fetchConversationDetail(
    fetchImpl: typeof fetch,
    headers: Record<string, string>,
    id: string
): Promise<ConversationDetail> {
    return conversationDetailSchema.parse(
        failUnreadableCards(
            await getJson(
                fetchImpl,
                `${CONVERSATIONS_URL}/${encodeURIComponent(id)}`,
                headers
            )
        )
    );
}
